import { useEffect, useMemo, useState } from "react";

const DEFAULT_SEND_FROM = "Ironfleet<noreply@ironfleet.ai>";

export function TestSendModal({
  stepName,
  defaultEmail,
  defaultSendFrom,
  dynamicAttributes,
  error,
  busy,
  onConfirm,
  onClose,
}: {
  stepName: string;
  defaultEmail: string;
  defaultSendFrom?: string;
  dynamicAttributes: string[];
  error?: string | null;
  busy: boolean;
  onConfirm: (to: string, dynamicTemplateData: Record<string, string>, sendFrom: string) => void;
  onClose: () => void;
}) {
  const [to, setTo] = useState(defaultEmail);
  const [sendFrom, setSendFrom] = useState(defaultSendFrom || DEFAULT_SEND_FROM);
  const [dynamicValues, setDynamicValues] = useState<Record<string, string>>({});

  useEffect(() => {
    setTo(defaultEmail);
    setSendFrom(defaultSendFrom || DEFAULT_SEND_FROM);
    setDynamicValues({});
  }, [defaultEmail, defaultSendFrom, stepName]);

  const fields = useMemo(
    () => [...new Set(dynamicAttributes)].sort((a, b) => a.localeCompare(b)),
    [dynamicAttributes],
  );

  function setDynamicField(name: string, value: string) {
    setDynamicValues((prev) => ({ ...prev, [name]: value }));
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="test-send-title">
      <div className="modal-card send-preview-modal test-send-modal">
        <h3 id="test-send-title">Test send — {stepName}</h3>
        <p className="hint">
          Send a one-off test for this email only. Enter SendGrid dynamic fields below; empty fields use saved
          audience mappings when you send.
        </p>
        {error ? <p className="campaign-builder-error" role="alert">{error}</p> : null}
        <label className="campaign-builder-field">
          <span>Send from</span>
          <input
            type="text"
            value={sendFrom}
            onChange={(e) => setSendFrom(e.target.value)}
            placeholder={DEFAULT_SEND_FROM}
            disabled={busy}
          />
        </label>
        <label className="campaign-builder-field">
          <span>Test recipient</span>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            disabled={busy}
          />
        </label>

        {fields.length ? (
          <div className="test-send-dynamic-fields">
            <div className="test-send-dynamic-fields-head">
              <h4>Dynamic template fields</h4>
              <p className="hint">{fields.length} field{fields.length === 1 ? "" : "s"} from SendGrid template</p>
            </div>
            {fields.map((name) => (
              <label key={name} className="campaign-builder-field test-send-dynamic-field">
                <span><code>{name}</code></span>
                <input
                  type="text"
                  value={dynamicValues[name] ?? ""}
                  onChange={(e) => setDynamicField(name, e.target.value)}
                  placeholder="Value for test send"
                  disabled={busy}
                />
              </label>
            ))}
          </div>
        ) : (
          <p className="hint test-send-no-dynamic">Validate or refresh dynamic attributes on this email to capture template fields.</p>
        )}

        <div className="campaign-builder-actions">
          <button
            type="button"
            disabled={busy || !to.trim() || !sendFrom.trim()}
            onClick={() => onConfirm(to.trim(), dynamicValues, sendFrom.trim())}
          >
            {busy ? "Sending…" : "Send test"}
          </button>
          <button type="button" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
